import { useContext } from "react";
import "./SearchModal.scss";
import PropTypes from "prop-types";
import classNames from "classnames";
import Modal from "react-modal";
import { useLocation } from "react-router";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import {
	AsyncHotelOnlySearchFormContainer,
	AsyncSmartDPSearchFormContainer,
	AsyncStopoverSearchFormContainer,
} from "app/routes";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { FormattedMessage } from "react-intl";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import IconClose from "app/pages/.shared/IconClose";
import useReadyOnTransitionEnd from "app/utils/hooks/useReadyOnTransitionEnd";

// Filet de sécurité si transitionend ne se déclenche jamais. Valeur volontairement large et fixe,
// pas liée à la durée nominale de la transition (qui pourrait se déclencher trop tôt sur un appareil lent).
const READY_FALLBACK_MS = 2000;

const SearchForm = ({ isStopover, isSDP, isHotelOnly, onSubmit, initialFocusField }) => {
	if (isStopover) {
		return (
			<AsyncStopoverSearchFormContainer
				onSubmit={onSubmit}
				initialFocusField={initialFocusField}
			/>
		);
	}
	if (isSDP) {
		return (
			<AsyncSmartDPSearchFormContainer
				onSubmit={onSubmit}
				initialFocusField={initialFocusField}
			/>
		);
	}
	if (isHotelOnly) {
		return <AsyncHotelOnlySearchFormContainer onSubmit={onSubmit} />;
	}
	return null;
};

SearchForm.propTypes = {
	isStopover: PropTypes.bool,
	isSDP: PropTypes.bool,
	isHotelOnly: PropTypes.bool,
	onSubmit: PropTypes.func,
	initialFocusField: PropTypes.string,
};

const SearchModal = ({ isOpen, onClose, initialFocusField }) => {
	const { pathname } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);
	const [handleOverlayRef, isReady, resetReady] = useReadyOnTransitionEnd(
		"opacity",
		READY_FALLBACK_MS
	);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const isStopover = pathname.includes("stopover");
	const isSDP = pathname.includes("sdp");
	const isHotelOnly = pathname.includes("hotelonly");

	const canAutoFocus = !initialFocusField || isReady;

	// En sautant sur un champ (ex: lien "Choisir une autre classe"), son volet s'ouvre juste après :
	// on atténue l'ouverture de cette modale pour éviter deux animations qui s'enchaînent.
	const isJumpingToField = isMobile && Boolean(initialFocusField);

	return (
		<Modal
			isOpen={isOpen}
			closeTimeoutMS={200}
			overlayRef={handleOverlayRef}
			onAfterClose={resetReady}
			onRequestClose={onClose}
			overlayClassName="search-modal__overlay"
			className={classNames("search-modal__content", {
				"search-modal__content--discreet": isJumpingToField,
			})}
		>
			{!isMobile && <HeaderContainer showMenuItems showDialPhone={false} showBackBtn />}

			{isMobile && !isJumpingToField && (
				<section className="search-modal__title">
					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold component="h3">
						<FormattedMessage id="sdp.search.home.input.label" />
					</Typography>

					<IconClose
						width={24}
						height={24}
						onClick={onClose}
						className="search-modal__title-close-icon"
					/>
				</section>
			)}

			{canAutoFocus && (
				<SearchForm
					isSDP={isSDP}
					isStopover={isStopover}
					isHotelOnly={isHotelOnly}
					onSubmit={onClose}
					initialFocusField={initialFocusField}
				/>
			)}
		</Modal>
	);
};

SearchModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	initialFocusField: PropTypes.string,
};

export default SearchModal;
